/**
 * §9.5 Technical Support — 6 action cards.
 *
 * 06/2026 — Existing customers no longer get pushed to `/login` from
 * marketing pages (that flow redirects them out of the corporate site
 * and creates confusion).  Instead, cards that previously routed to
 * `/login` (Submit a support request, Customer portal sign-in) now
 * open a lightweight inline support form modal that submits to
 * `POST /api/leads` with kind=`support`.  Info-only cards
 * (Docs, KB, Billing) still link to their in-page anchors on
 * /resources; the "Report a technical issue" card opens the same
 * support modal pre-populated with the issue lane.
 */

import React, { useState } from 'react'
import { toast } from 'sonner'
import {
  TicketCheck, BookOpen, HelpCircle, CreditCard, AlertTriangle, LogIn,
  Sparkles, ArrowRight, Info, X, Loader2, MessageCircle,
} from 'lucide-react'
import {
  Container, Eyebrow, SectionHeading, GhostButton,
} from '@/components/landing/design-system'
import { SUPPORT_ACTIONS } from '../data'
import { TONE } from '../_shared'

const ICONS = { TicketCheck, BookOpen, HelpCircle, CreditCard, AlertTriangle, LogIn }

export default function Support() {
  const [modal, setModal] = useState(null)  // null or SUPPORT_ACTIONS entry

  const onCardClick = (a) => (e) => {
    if (a.modal) {
      e.preventDefault()
      setModal(a)
    }
  }

  return (
    <section id="support" className="relative py-24 md:py-28 scroll-mt-24">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Technical Support</Eyebrow>
          <SectionHeading align="center"
            title={<>Existing customer?{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">Skip the form.</span></>}
            subtitle="Pick the right lane and we&apos;ll get you there faster than a generic contact form ever could."
          />
        </div>

        <div className="rounded-2xl border border-blue-400/25 bg-blue-400/[0.05] px-5 md:px-6 py-4 backdrop-blur-xl flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
          <Info className="h-4 w-4 text-blue-400 shrink-0" />
          <p className="text-sm text-zinc-100 leading-relaxed">
            <span className="font-semibold text-white">Support tickets are prioritised by plan tier.</span>{' '}
            Professional and Enterprise customers land in the priority lane by default — typed metadata from your workspace is attached automatically for faster triage.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {SUPPORT_ACTIONS.map((a) => {
            const tone = TONE[a.tone] || TONE.orange
            const Icon = ICONS[a.iconKey] || Sparkles
            const Wrapper = a.modal ? 'button' : 'a'
            const props = a.modal
              ? { type: 'button', onClick: onCardClick(a) }
              : { href: a.href }
            return (
              <Wrapper key={a.id} {...props}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px] overflow-hidden flex flex-col gap-3 text-left">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                <div className="relative flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <div className="text-[14.5px] font-semibold text-white leading-tight">{a.label}</div>
                </div>
                <p className="relative text-[12.5px] text-zinc-400 leading-relaxed">{a.body}</p>
                <div className="relative mt-auto pt-1 inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 group-hover:text-brand-gold transition-colors">
                  {a.ctaLabel} <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Wrapper>
            )
          })}
        </div>

        <div className="flex items-center justify-center">
          <GhostButton href="mailto:support@kalkiai.trade" trailingIcon>
            Or email support@kalkiai.trade <ArrowRight className="h-3 w-3" />
          </GhostButton>
        </div>
      </Container>

      <SupportModal
        open={!!modal}
        preset={modal}
        onClose={() => setModal(null)}
      />
    </section>
  )
}

// ─── Inline Support Form Modal ────────────────────────────────────
function SupportModal({ open, preset, onClose }) {
  const [name, setName]       = useState('')
  const [email, setEmail]     = useState('')
  const [company, setCompany] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy]       = useState(false)
  const [sent, setSent]       = useState(false)

  const topic = preset?.label || 'Technical Support'
  const lane  = preset?.id    || 'support'

  React.useEffect(() => {
    if (open) {
      setSent(false)
      setName('')
      setEmail('')
      setCompany('')
      setMessage('')
    }
  }, [open])

  if (!open || !preset) return null

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Full name and a valid email are required.')
      return
    }
    setBusy(true)
    try {
      const r = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind:    'support',
          lane,
          topic,
          name:    name.trim(),
          email:   email.trim(),
          company: company.trim(),
          notes:   message.trim(),
        }),
      })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j?.error || 'Submission failed')
      }
      setSent(true)
      toast.success('Support request received', { description: 'Our team will reach out shortly.' })
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg my-8 rounded-3xl border border-white/10 bg-[#0b0b0f] shadow-[0_40px_120px_-20px_rgba(59,130,246,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full border border-white/10 bg-white/[0.04] text-zinc-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition"
          aria-label="Close support form"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-7 pt-8 pb-5 border-b border-white/5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-brand-gold mb-2 flex items-center gap-1.5">
            <MessageCircle className="h-3 w-3" /> Support Request
          </div>
          <div className="text-xl md:text-2xl font-semibold text-white leading-tight">{topic}</div>
          <div className="text-[13px] text-zinc-400 mt-1">{preset.body}</div>
        </div>

        {sent ? (
          <div className="px-7 py-10 text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <MessageCircle className="h-6 w-6 text-emerald-400" />
            </div>
            <div className="text-lg font-semibold text-white">Request received</div>
            <div className="text-sm text-zinc-400 max-w-sm mx-auto">
              A support engineer will reach out to <span className="text-white">{email}</span> within one business day.
            </div>
            <div>
              <GhostButton onClick={onClose} size="md">Close</GhostButton>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 py-6 space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Full name" value={name} onChange={setName} placeholder="Jane Doe" required />
              <Field label="Work email" value={email} onChange={setEmail} placeholder="you@company.com" type="email" required />
            </div>
            <Field label="Company" value={company} onChange={setCompany} placeholder="Acme Trading Ltd. (optional)" />
            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Message<span className="text-brand-gold">*</span></span>
              <textarea
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`Describe your ${topic.toLowerCase()} request — include workspace, plan, and any relevant context.`}
                required
                className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-gold/50 focus:ring-2 focus:ring-brand-gold/20 resize-none"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-gold to-[#FFE17A] text-black font-semibold py-3 text-[15px] shadow-[0_10px_40px_-10px_rgba(255,193,7,0.55)] hover:shadow-[0_14px_46px_-8px_rgba(255,193,7,0.75)] transition-all disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Submit request <ArrowRight className="h-4 w-4" /></>)}
            </button>
            <div className="text-[11px] text-zinc-500 text-center">
              Or email <span className="text-white font-semibold">support@kalkiai.trade</span> — same triage lane.
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

const Field = ({ label, value, onChange, type = 'text', placeholder = '', required = false }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}{required && <span className="text-brand-gold">*</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-gold/50 focus:ring-2 focus:ring-brand-gold/20"
    />
  </label>
)
